import Header from "@components/Header/Header"
import NumericKeyboard from "@components/NumericKeyboard/NumericKeyboard"
import PrimaryButton from "@components/PrimaryButton/PrimaryButton"
import { toCurrencyFormat } from "@lib/utils"
import { useCallback, useState } from "react"
import { useLocation, useNavigate } from "react-router"
import {
    AmountDisplayContainer,
    AmountText,
    AmountWrapper,
    BankCardContainer,
    BankIcon,
    BankIconWrapper,
    BankName,
    BottomSection,
    Container,
    ContentContainer,
    Divider,
    ErrorMessageContainer,
    QuickAmountButton,
    QuickAmountContainer,
    QuickAmountText,
    TopSection
} from "./TopUpBankInputAmount.styles"
import { useNumericKeyboard } from "@hooks/useNumericKeyboard"
import { useTranslation } from "react-i18next"
import useVirtualAccountDeposit from "@hooks/useVirtualAccountDeposit"
import LoadingModal from "@components/LoadingModal/LoadingModal"
import Snackbar from "@components/Snackbar/Snackbar"

const BankInformationCard = ({
    name,
    iconUrl
}: {
    name: string
    iconUrl: string
}) => {
    return (
        <BankCardContainer>
            <BankIconWrapper>
                <BankIcon
                    src={iconUrl} 
                    alt={`${name} icon`} 
                />
            </BankIconWrapper>
            <BankName>{name}</BankName>
        </BankCardContainer>
    )
}

const AmountDisplay = ({
    amount
}: {
    amount: number
}) => {
    return (
        <AmountDisplayContainer>
            <AmountText>{`Rp${toCurrencyFormat(amount)}`}</AmountText>
        </AmountDisplayContainer>
    )
}

const quickAmounts = [100000, 200000, 300000, 500000, 1000000]

const QuickAmountButtons = ({
    amounts,
    onAmountClick
}: {
    amounts: number[]
    onAmountClick: (amount: number) => void
}) => {
    return (
        <QuickAmountContainer>
            {amounts.map((amount) => (
                <QuickAmountButton
                    key={amount} 
                    onClick={() => onAmountClick(amount)}
                >
                    <QuickAmountText>
                        {`Rp${toCurrencyFormat(amount)}`}
                    </QuickAmountText>
                </QuickAmountButton>
            ))}
        </QuickAmountContainer>
    )
}

const TopUpBankInputAmount = () => {
    const { bank, amount: initialAmount } = useLocation().state
    const maxAmount = bank.max_amount
    const minAmount = bank.min_amount
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { createVirtualAccountDeposit, isLoading, error } = useVirtualAccountDeposit()
    
    // Add a counter to force Snackbar to re-render when the same error occurs multiple times
    const [errorCounter, setErrorCounter] = useState(0)

    const { amount, setAmount, handleKeyboardPress, isKeyboardPressedForTheFirstTime } = useNumericKeyboard(initialAmount)

    const handleContinue = useCallback(async () => {
        try {
            const response = await createVirtualAccountDeposit(bank.id, amount)
            if (response) {
                const virtualAccount = response.account_number
                const expiryDate = response.expiry_date
                navigate('/top-up-bank-virtual-account', { state: { bank, amount, virtualAccount, expiryDate }})   
            } else {
                throw 'Failed to create virtual account'
            }
        } catch (error) {
            // Increment error counter to force Snackbar to re-render
            setErrorCounter(prev => prev + 1)
        }
    }, [bank.id, amount, createVirtualAccountDeposit, setErrorCounter])

    return (
        <Container>
            <Header title={t('topUpBank.inputAmount.header')} showBackButton onBackClick={() => navigate('/top-up-bank-select')} />
            <ContentContainer>
                <TopSection>
                    <BankInformationCard name={bank.name} iconUrl={bank.iconUrl} />
                    <AmountWrapper>
                        <AmountDisplay amount={amount} />
                    </AmountWrapper>
                    <Divider />
                    <ErrorMessageContainer>
                        {maxAmount && isKeyboardPressedForTheFirstTime && amount > maxAmount && 
                        <span>
                            {t('topUpBank.inputAmount.validation.maxAmount', { amount: toCurrencyFormat(maxAmount) })}
                        </span>}
                        {minAmount && isKeyboardPressedForTheFirstTime && amount < minAmount && 
                        <span>
                            {t('topUpBank.inputAmount.validation.minAmount', { amount: toCurrencyFormat(minAmount) })}
                        </span>}
                    </ErrorMessageContainer>
                    <QuickAmountButtons amounts={quickAmounts} onAmountClick={(amount) => setAmount(amount)} />
                </TopSection>
                <BottomSection>
                    <NumericKeyboard 
                        onChange={(amount) => handleKeyboardPress(amount)} 
                        emptySpaceButton="thousand"
                    />
                    <PrimaryButton
                        disabled={isLoading || amount < minAmount || amount > maxAmount}
                        onClick={handleContinue}
                    >
                        {t('topUpBank.inputAmount.continueButton')}
                    </PrimaryButton>
                </BottomSection>
            </ContentContainer>
            <LoadingModal visible={isLoading} />
            <Snackbar 
                key={`error-${errorCounter}`}
                message={error || ''}
                isVisible={!!error}
                duration={5000}
                width="90%"
                onClose={() => {}}
            />
        </Container>
    )
}

export default TopUpBankInputAmount